/**
 * RecordObjectListTab — embeds the full CRM listing table (search, filters,
 * sortable columns, bulk actions, row-click → slideout) inside a record's
 * detail tab, scoped to entities ASSOCIATED with the current record.
 *
 * Reuses the standard list page via `defaultFilters.associated_with_entity_id`
 * (the same backend-native association scoping the list API already supports),
 * so e.g. the Emails tab shows the email threads linked to this contact and
 * the Meetings tab shows its meetings.
 *
 * Must be rendered inside a height-bounded flex container (FullPageTabs "fill"
 * mode) so the virtualized table gets a definite height.
 */

import { type ReactNode, useMemo } from 'react';

import type { HeaderAction } from '@/lib/base/page-layout/page-layout';

import type { ColumnConfig, ListPageConfig } from '../../core/types';
import { CRMObjectListPage } from '../../list/components/crm-object-list-page';

interface RecordObjectListTabProps {
  /** The base list config for the associated entity type (e.g. emailThreadListConfig). */
  baseConfig: ListPageConfig;
  /** The record whose associated entities are listed. */
  entityId: string;
  /**
   * The host record's entity type (e.g. `ahoy_contact`). Used to drop the
   * association column that points back to the host — on a contact's Emails
   * tab the "Contact" column would otherwise repeat this same contact on
   * every row, which is just noise.
   */
  hostEntityType: string;
  /** Plural count label, e.g. "threads for Pamela Adams". */
  countLabel?: string;
  /** Singular count label, e.g. "thread for Pamela Adams". */
  countLabelSingular?: string;
  /**
   * Optional primary create handler forwarded to CRMObjectListPage's Add
   * button + empty-state "create first" action. Omitted by default so
   * system-created list tabs (emails, contacts) render no create affordance.
   */
  onCreateClick?: () => void;
  /**
   * When true, the create affordances driven by `onCreateClick` (the empty-state
   * "create first" CTA, and the Add button in non-embedded use) render disabled
   * rather than active — for a create flow that is momentarily unavailable.
   */
  createDisabled?: boolean;
  /**
   * Tooltip for the Add button when it is disabled by `createDisabled` (the
   * user has permission but the flow is momentarily unavailable). Names the
   * transient reason — e.g. "Loading…". Ignored when the user actually lacks
   * create permission (that shows its own permission message).
   */
  createDisabledTooltip?: string;
  /** Extra toolbar actions inserted before the primary Add button. */
  extraHeaderActions?: HeaderAction[];
  /** Custom content rendered in the list toolbar, before search/filter. */
  toolbarLeading?: ReactNode;
  /** Custom content pinned to the far right of the list toolbar row. */
  toolbarTrailing?: ReactNode;
}

/**
 * Is `column` an association that points back to the host record's own type?
 * Association types follow the `ahoy_<source>_to_<target>` convention, so the
 * host-redundant column is the one whose target segment matches the host's
 * kind (e.g. host `ahoy_contact` → drop `ahoy_email_thread_to_contact`).
 */
function isHostRedundantAssociation(
  column: ColumnConfig,
  hostEntityType: string
): boolean {
  if (column.kind !== 'association' || !column.name) {
    return false;
  }
  // `ahoy_contact` → `_to_contact`. Anchored on `_to_` so we don't match a
  // longer target that merely ends with the host kind (e.g. `_to_meeting`
  // must not match the host `meeting` against `_to_meeting_notes`).
  const hostKind = hostEntityType.replace(/^ahoy_/, '');
  return column.name.endsWith(`_to_${hostKind}`);
}

export function RecordObjectListTab({
  baseConfig,
  entityId,
  hostEntityType,
  countLabel,
  countLabelSingular,
  onCreateClick,
  createDisabled,
  createDisabledTooltip,
  extraHeaderActions,
  toolbarLeading,
  toolbarTrailing,
}: RecordObjectListTabProps) {
  // Scope the list to this record's associations. `associated_with_entity_id`
  // is preserved through `useListState` into the query (it's part of
  // CrmObjectFilters), so the embedded list only shows linked entities. We
  // also drop the column that links back to the host record (always this same
  // record — redundant in an embedded context).
  const scopedConfig = useMemo<ListPageConfig>(
    () => ({
      ...baseConfig,
      columns: baseConfig.columns.filter(
        col => !isHostRedundantAssociation(col, hostEntityType)
      ),
      defaultFilters: {
        ...baseConfig.defaultFilters,
        associated_with_entity_id: entityId,
      },
    }),
    [baseConfig, entityId, hostEntityType]
  );

  // `embedded` drops the list page's title row but keeps the standard list
  // chrome — crucially the toolbar's `px-4` inset and the table's edge-to-edge
  // content. `lg:-mx-6` then bleeds the whole panel back across the record
  // shell's horizontal padding (outer `px-6` on the left, the main section's
  // `lg:pr-6` on the right — both 24px), so the table runs flush from the nav
  // divider to the details rail while the toolbar keeps its normal inset. The
  // net result matches a standalone list page exactly.
  return (
    <div className='flex flex-1 min-h-0 flex-col lg:-mx-6'>
      <CRMObjectListPage
        config={scopedConfig}
        embedded
        countLabel={countLabel}
        countLabelSingular={countLabelSingular}
        onCreateClick={onCreateClick}
        createDisabled={createDisabled}
        createDisabledTooltip={createDisabledTooltip}
        extraHeaderActions={extraHeaderActions}
        toolbarLeading={toolbarLeading}
        toolbarTrailing={toolbarTrailing}
      />
    </div>
  );
}
